import { useState, useEffect } from 'react';
import { SidePanel, SidePanelFooter } from './SidePanel';
import { Select } from './FormField';
import { Button } from '../ui/Button';
import { FormMultiSelect, FormMultiSelectOption } from '../ui/FormMultiSelect';
import {
  intelligenceApi,
  initiativeApi,
  commentApi,
  productApi,
  subProductApi,
  classificationApi,
  statusApi,
  userApi,
  tagApi,
  fiscalYearApi,
  fieldConfigApi,
  type AiInitiativeSuggestion,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFieldConfig } from '../../hooks/useFieldConfig';
import { useAppStore } from '../../stores/appStore';

import { SparklesIcon } from '@heroicons/react/24/outline';

interface AiInitiativeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'input' | 'review';

export function AiInitiativeModal({ isOpen, onClose, onSuccess }: AiInitiativeModalProps) {
  const { user } = useAuth();
  const fc = useFieldConfig();
  const setStoreFieldConfigs = useAppStore((s) => s.setFieldConfigs);
  const aiProviderLabel = useAppStore((s) => s.aiProviderLabel);

  // Step state
  const [step, setStep] = useState<Step>('input');
  const [description, setDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  // Reference data
  const [products, setProducts] = useState<any[]>([]);
  const [subProducts, setSubProducts] = useState<any[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [quarters, setQuarters] = useState<Array<{ key: string; fullLabel: string }>>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Form data (review step)
  const [formData, setFormData] = useState({
    title: '',
    productId: '',
    subProductId: '',
    classificationId: '',
    statusId: '',
    userId: '',
    startQuarter: '',
    endQuarter: '',
    tagIds: [] as string[],
  });

  // Fields where AI found and filled a value — shown with a green check
  const [matched, setMatched] = useState<Set<string>>(new Set());
  const [contextComment, setContextComment] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('input');
      setDescription('');
      setGenerateError('');
      setContextComment('');
      setMatched(new Set());
      setErrors({});
    }
  }, [isOpen]);

  // Load reference data on open
  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setIsLoadingData(true);
      try {
        const [prodData, classData, statusData, userData, tagData, fyData, fcData] = await Promise.all([
          productApi.getAll(),
          classificationApi.getAll(),
          statusApi.getAll(),
          userApi.getAll(),
          tagApi.getAll(),
          fiscalYearApi.getAll(),
          fieldConfigApi.getAll().catch(() => []),
        ]);
        setProducts(prodData);
        setClassifications(classData);
        setStatuses(statusData);
        setUsers(userData);
        setTags(tagData);
        if (fcData.length > 0) setStoreFieldConfigs(fcData);

        const allQuarters: Array<{ key: string; fullLabel: string }> = [];
        fyData.forEach((fy: any) => {
          if (fy.quarters) allQuarters.push(...fy.quarters.map((q: any) => ({ key: q.key, fullLabel: q.fullLabel })));
        });
        setQuarters(allQuarters);
      } finally {
        setIsLoadingData(false);
      }
    };
    load();
  }, [isOpen]);

  // Load sub-products when product changes
  useEffect(() => {
    if (formData.productId) {
      subProductApi.getByProduct(formData.productId).then(setSubProducts).catch(() => setSubProducts([]));
    } else {
      setSubProducts([]);
    }
  }, [formData.productId]);

  const handleGenerate = async () => {
    if (!description.trim() || description.trim().length < 10) {
      setGenerateError('Please provide a description (at least 10 characters).');
      return;
    }
    setGenerateError('');
    setIsGenerating(true);
    try {
      const { suggestion } = await intelligenceApi.createInitiative(description);
      applysuggestion(suggestion);
      setStep('review');
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate suggestion. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const applysuggestion = (s: AiInitiativeSuggestion) => {
    const m = new Set<string>();

    if (s.title) m.add('title');
    if (s.classificationId) m.add('classificationId');
    if (s.productId) m.add('productId');
    if (s.subProductId) m.add('subProductId');
    if (s.startQuarter) m.add('startQuarter');
    if (s.endQuarter) m.add('endQuarter');
    // Owner: if AI found a user, mark as matched; otherwise silently fall back to current user
    if (s.userId) m.add('userId');

    setMatched(m);

    setFormData({
      title: s.title || '',
      productId: s.productId || '',
      subProductId: s.subProductId || '',
      classificationId: s.classificationId || '',
      statusId: s.statusId || statuses.find((st: any) => st.isDefault)?.id || statuses[0]?.id || '',
      userId: s.userId || user?.id || '',
      startQuarter: s.startQuarter || '',
      endQuarter: s.endQuarter || '',
      tagIds: [],
    });
    setContextComment(s.contextComment || '');
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.subProductId) newErrors.subProductId = `${fc.label('subProduct')} is required`;
    if (!formData.statusId) newErrors.statusId = 'Status is required';
    if (!formData.startQuarter) newErrors.startQuarter = 'Start period is required';
    if (formData.endQuarter && formData.startQuarter && formData.endQuarter < formData.startQuarter) {
      newErrors.endQuarter = 'End must be after start';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const initiative = await initiativeApi.create({
        title: formData.title.trim(),
        subProductId: formData.subProductId,
        classificationId: fc.isVisible('classification') ? formData.classificationId || null : null,
        statusId: formData.statusId,
        userId: formData.userId || null,
        startQuarter: formData.startQuarter,
        endQuarter: fc.isVisible('end') ? formData.endQuarter || null : null,
        createdBy: user?.id,
        lastModifiedBy: user?.id,
        tags: fc.isVisible('tags') ? formData.tagIds : [],
      });

      // Post context comment if Claude provided one
      if (contextComment.trim() && initiative?.id) {
        await commentApi.create(initiative.id, contextComment.trim()).catch((err) => {
          console.warn('Failed to post context comment:', err);
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed to create initiative' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const greenCheck = (key: string) =>
    matched.has(key) ? (
      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        AI matched
      </p>
    ) : null;

  const productOptions = products.map((p) => ({ value: p.id, label: p.name }));
  const subProductOptions = subProducts.map((sp) => ({ value: sp.id, label: sp.name }));
  const classificationOptions = classifications.map((c) => ({ value: c.id, label: c.name }));
  const statusOptions = statuses.map((s) => ({ value: s.id, label: s.name }));
  const userOptions = users.map((u) => ({ value: u.id, label: u.name }));
  const quarterOptions = quarters.map((q) => ({ value: q.key, label: q.fullLabel }));
  const tagOptions: FormMultiSelectOption[] = tags.map((t) => ({ value: t.id, label: t.name }));

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={step === 'input' ? 'Create with AI' : 'Review AI suggestion'}
    >
      <div className="flex flex-col flex-1 min-h-0">
        {/* Tab-like step indicator */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border-subtle bg-amber-50 flex-shrink-0">
          <SparklesIcon className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <div className="flex items-center gap-2 text-sm">
            <span className={step === 'input' ? 'font-semibold text-amber-700' : 'text-text-secondary'}>
              1. Describe
            </span>
            <span className="text-text-tertiary">→</span>
            <span className={step === 'review' ? 'font-semibold text-amber-700' : 'text-text-secondary'}>
              2. Review &amp; Save
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* ── Step 1: Input ─────────────────────────────── */}
          {step === 'input' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Describe the initiative
                </label>
                <textarea
                  rows={6}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. We need to rebuild the onboarding flow for mobile users — it's blocking Q2 growth targets. Sarah from the Platform team should own this."
                  className="w-full rounded-md border border-border-default px-3 py-2 text-sm bg-surface-default text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:border-border-focus focus:ring-border-focus/20 resize-none"
                />
                {generateError && (
                  <p className="mt-2 text-sm text-red-600">{generateError}</p>
                )}
              </div>
              <p className="text-xs text-text-secondary">
                AI will extract the title, suggest a classification, owner, timeframe, and product — all matched to your actual data.
              </p>
            </div>
          )}

          {/* ── Step 2: Review ───────────────────────────── */}
          {step === 'review' && !isLoadingData && (
            <div className="space-y-4">
              {/* Title */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-text-primary">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  maxLength={200}
                  className={`w-full rounded-md border px-3 py-2 text-sm bg-surface-default text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 ${errors.title ? 'border-red-500 focus:ring-red-500/20' : 'border-border-default focus:border-border-focus focus:ring-border-focus/20'}`}
                />
                {greenCheck('title')}
                {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
              </div>

              {/* Product / Sub-product */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Select
                    label={fc.label('product')}
                    name="productId"
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: e.target.value, subProductId: '' })}
                    options={productOptions}
                    placeholder={`Select a ${fc.label('product').toLowerCase()}`}
                    required
                    error={errors.productId}
                  />
                  {greenCheck('productId')}
                </div>
                <div>
                  <Select
                    label={fc.label('subProduct')}
                    name="subProductId"
                    value={formData.subProductId}
                    onChange={(e) => setFormData({ ...formData, subProductId: e.target.value })}
                    options={subProductOptions}
                    placeholder={`Select a ${fc.label('subProduct').toLowerCase()}`}
                    required
                    error={errors.subProductId}
                    disabled={!formData.productId}
                  />
                  {greenCheck('subProductId')}
                </div>
              </div>

              {/* Classification + Status */}
              <div className="grid grid-cols-2 gap-4">
                {fc.isVisible('classification') && (
                  <div>
                    <Select
                      label={fc.label('classification')}
                      name="classificationId"
                      value={formData.classificationId}
                      onChange={(e) => setFormData({ ...formData, classificationId: e.target.value })}
                      options={classificationOptions}
                      placeholder={`Select a ${fc.label('classification').toLowerCase()}`}
                      required={fc.isRequired('classification')}
                      error={errors.classificationId}
                    />
                    {greenCheck('classificationId')}
                  </div>
                )}
                <Select
                  label={fc.label('status')}
                  name="statusId"
                  value={formData.statusId}
                  onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
                  options={statusOptions}
                  placeholder="Select a status"
                  required
                  error={errors.statusId}
                />
              </div>

              {/* Owner */}
              <div>
                <Select
                  label={fc.label('owner')}
                  name="userId"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  options={userOptions}
                  placeholder={`Select an ${fc.label('owner').toLowerCase()}`}
                  required={fc.isRequired('owner')}
                  error={errors.userId}
                />
                {greenCheck('userId')}
              </div>

              {/* Start / End */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Select
                    label={fc.label('start')}
                    name="startQuarter"
                    value={formData.startQuarter}
                    onChange={(e) => setFormData({ ...formData, startQuarter: e.target.value })}
                    options={quarterOptions}
                    placeholder="Select start period"
                    required
                    error={errors.startQuarter}
                  />
                  {greenCheck('startQuarter')}
                </div>
                {fc.isVisible('end') && (
                  <div>
                    <Select
                      label={fc.label('end')}
                      name="endQuarter"
                      value={formData.endQuarter}
                      onChange={(e) => setFormData({ ...formData, endQuarter: e.target.value })}
                      options={quarterOptions}
                      placeholder="Select end period"
                      required={fc.isRequired('end')}
                      error={errors.endQuarter}
                      helpText={!fc.isRequired('end') ? 'Optional' : undefined}
                    />
                    {greenCheck('endQuarter')}
                  </div>
                )}
              </div>

              {/* Tags */}
              {fc.isVisible('tags') && tagOptions.length > 0 && (
                <FormMultiSelect
                  label={fc.label('tags')}
                  options={tagOptions}
                  selected={formData.tagIds}
                  onChange={(selected) => setFormData({ ...formData, tagIds: selected })}
                  placeholder={`Select ${fc.label('tags').toLowerCase()}...`}
                  helpText="Optional"
                />
              )}

              {/* Context comment preview */}
              {contextComment && (
                <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
                  <p className="text-xs font-medium text-blue-700 mb-1 flex items-center gap-1">
                    <SparklesIcon className="w-3.5 h-3.5" />
                    AI context comment (will be posted automatically)
                  </p>
                  <p className="text-xs text-blue-800">{contextComment}</p>
                </div>
              )}

              {errors.submit && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3">
                  <p className="text-sm text-red-800">{errors.submit}</p>
                </div>
              )}
            </div>
          )}

          {step === 'review' && isLoadingData && (
            <div className="flex items-center justify-center py-12 text-text-secondary text-sm">
              Loading form data…
            </div>
          )}
        </div>

        {/* Footer */}
        <SidePanelFooter>
          {step === 'review' && (
            <Button type="button" variant="secondary" onClick={() => setStep('input')} disabled={isSubmitting}>
              Back
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={onClose} disabled={isGenerating || isSubmitting}>
            Cancel
          </Button>
          {step === 'input' ? (
            <Button
              type="button"
              variant="primary"
              onClick={handleGenerate}
              disabled={isGenerating || description.trim().length < 10}
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Generating…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <SparklesIcon className="w-4 h-4" />
                  Generate with {aiProviderLabel}
                </span>
              )}
            </Button>
          ) : (
            <Button type="button" variant="primary" onClick={handleSubmit} disabled={isSubmitting || isLoadingData}>
              {isSubmitting ? 'Creating…' : 'Create initiative'}
            </Button>
          )}
        </SidePanelFooter>
      </div>
    </SidePanel>
  );
}
